import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { CommonService } from 'wire-frame';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { SidebarModule } from 'primeng/sidebar';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { CardsService } from '../../../service/admin/cards.service';
import { WorkContextService } from '../../../service/admin/work-context.service';
import { WorkContextBarComponent } from '../../shared/work-context-bar/work-context-bar.component';
import { ToasterService } from '../../../service/common/toaster.service';

export interface CardSearchHit {
  id: number;
  number: string;
  status: string;
  total_dues: number;
  patient_count: number;
  patient_names?: string;
  patients: any[];
}

export interface PatientResultRow {
  key: string;
  patient: any;
  card: CardSearchHit;
  matched: boolean;
}

@Component({
  selector: 'app-cards',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    RouterModule,
    ConfirmDialogModule,
    DialogModule,
    DropdownModule,
    SidebarModule,
    WorkContextBarComponent,
  ],
  providers: [ConfirmationService, CommonService, CardsService],
  templateUrl: './cards.component.html',
  styleUrl: './cards.component.scss',
})
export class CardsComponent implements OnInit, OnDestroy {
  readonly paymentModeOptions = [
    { label: 'Cash', value: 'cash' },
    { label: 'Online / UPI', value: 'online' },
  ];

  searchTerm = '';
  isSearching = false;
  hasSearched = false;
  searchResults: CardSearchHit[] = [];
  patientRows: PatientResultRow[] = [];
  private searchSubject = new Subject<string>();
  private searchSub?: Subscription;
  private searchGeneration = 0;

  drawerVisible = false;
  activeCard: any = null;
  drawerLoading = false;
  drawerPatients: any[] = [];
  highlightPatientId: number | null = null;
  settlePatient: any = null;

  createDialogVisible = false;
  createCardNumber = '';
  createBusy = false;

  moveDialogVisible = false;
  moveBusy = false;
  movePatient: any = null;
  moveFromCard: any = null;
  moveOption: 'existing' | 'new' = 'existing';
  moveTargetQuery = '';
  moveTargetCard: any = null;
  moveTargetResults: any[] = [];
  moveTargetSearching = false;
  moveNewCardNumber = '';
  suggestedCardNumber = '';
  suggestingCardNumber = false;
  private moveSearchGeneration = 0;
  private moveSearchSubject = new Subject<string>();
  private moveSearchSub?: Subscription;

  private cardPaymentListenerAttached = false;

  constructor(
    public common: CommonService,
    private toaster: ToasterService,
    private confirmationService: ConfirmationService,
    private cardsService: CardsService,
    private workContext: WorkContextService,
  ) {
    this.common.breadCrum = {
      title: 'Cards',
      icon: 'credit-card',
      list: ['Cards'],
    };
  }

  ngOnInit(): void {
    this.searchSub = this.searchSubject
      .pipe(
        debounceTime(280),
        distinctUntilChanged(),
        switchMap((query) => {
          const q = (query || '').trim();
          if (q.length < 1) {
            this.isSearching = false;
            this.hasSearched = false;
            this.searchResults = [];
            this.patientRows = [];
            return of(null);
          }

          this.isSearching = true;
          const gen = ++this.searchGeneration;
          return this.cardsService.searchCards(q, 30).pipe(
            catchError((err) => {
              console.error('Card search error:', err);
              this.toaster.error('Could not search cards. Please try again.');
              return of({ success: false, data: [] });
            }),
            switchMap((res: any) => of({ res, gen })),
          );
        }),
      )
      .subscribe((payload: any) => {
        if (!payload) {
          return;
        }
        if (payload.gen !== this.searchGeneration) {
          return;
        }
        this.isSearching = false;
        this.hasSearched = true;
        this.searchResults = Array.isArray(payload.res?.data) ? payload.res.data : [];
        this.rebuildPatientRows();
      });

    this.moveSearchSub = this.moveSearchSubject
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => {
          const q = (query || '').trim();
          if (q.length < 1) {
            this.moveTargetSearching = false;
            this.moveTargetResults = [];
            return of(null);
          }

          this.moveTargetSearching = true;
          const gen = ++this.moveSearchGeneration;
          return this.cardsService.searchCards(q, 12).pipe(
            catchError(() => of({ success: false, data: [] })),
            switchMap((res: any) => of({ res, gen })),
          );
        }),
      )
      .subscribe((payload: any) => {
        if (!payload) {
          return;
        }
        if (payload.gen !== this.moveSearchGeneration) {
          return;
        }
        this.moveTargetSearching = false;
        const currentId = Number(this.moveFromCard?.id || 0);
        const cards = Array.isArray(payload.res?.data) ? payload.res.data : [];
        this.moveTargetResults = cards
          .filter((card: any) => Number(card.id) !== currentId)
          .map((card: any) => this.mapMoveTargetOption(card));
      });
  }

  ngOnDestroy(): void {
    this.searchSub?.unsubscribe();
    this.moveSearchSub?.unsubscribe();
  }

  onSearchInput(value: string): void {
    this.searchTerm = value;
    this.searchSubject.next(value);
    this.rebuildPatientRows();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchSubject.next('');
    this.patientRows = [];
  }

  trackByRowKey(_index: number, row: PatientResultRow): string {
    return row.key;
  }

  private rebuildPatientRows(): void {
    const query = (this.searchTerm || '').trim().toLowerCase();
    const rows: PatientResultRow[] = [];

    for (const card of this.searchResults || []) {
      const patients = Array.isArray(card.patients) ? card.patients : [];
      if (!patients.length) {
        rows.push({
          key: `card-empty-${card.id}`,
          patient: null,
          card,
          matched: true,
        });
        continue;
      }

      for (const patient of patients) {
        rows.push({
          key: `p-${patient.id}-c-${card.id}`,
          patient,
          card,
          matched: this.patientMatchesQuery(patient, card, query),
        });
      }
    }

    this.patientRows = rows.sort((a, b) => Number(b.matched) - Number(a.matched));
  }

  get matchedPatientCount(): number {
    return this.patientRows.filter((row) => row.patient && row.matched).length;
  }

  get resultCardCount(): number {
    return (this.searchResults || []).length;
  }

  openCard(cardOrId: any, highlightPatientId?: number): void {
    const card = this.resolveCardRef(cardOrId);
    const cardId = Number(card?.id ?? card?.card_id);
    if (!Number.isFinite(cardId) || cardId <= 0) {
      this.toaster.error('Card id is missing. Please search again.');
      return;
    }

    this.highlightPatientId = highlightPatientId ?? null;
    this.drawerLoading = true;
    this.settlePatient = null;
    this.activeCard = { ...card, id: cardId };
    this.drawerPatients = Array.isArray(card.patients)
      ? card.patients.map((p: any) => ({ ...p }))
      : [];
    this.drawerVisible = true;

    this.cardsService.getCard(cardId).subscribe({
      next: (res: any) => {
        this.drawerLoading = false;
        if (!res?.success || !res?.data) {
          this.toaster.error(res?.message || 'Could not load card details.');
          return;
        }
        this.activeCard = res.data;
        this.drawerPatients = Array.isArray(res.data.patients)
          ? res.data.patients.map((p: any) => ({ ...p }))
          : [];

        if (this.highlightPatientId) {
          const elId = `drawer-patient-${this.highlightPatientId}`;
          setTimeout(() => document.getElementById(elId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);
        }
      },
      error: (err) => {
        this.drawerLoading = false;
        console.error('Load card error:', err);
        this.toaster.error(err?.error?.message || 'Could not load card details.');
      },
    });
  }

  private resolveCardRef(cardOrId: any): any {
    if (cardOrId && typeof cardOrId === 'object') {
      const directId = Number(cardOrId.id ?? cardOrId.card_id);
      if (Number.isFinite(directId) && directId > 0) {
        return { ...cardOrId, id: directId };
      }
      const number = String(cardOrId.number || '').trim();
      if (number) {
        const match = (this.searchResults || []).find((c) => String(c.number || '').trim() === number);
        if (match) {
          return { ...match, ...cardOrId, id: Number(match.id) };
        }
      }
      return cardOrId;
    }

    const cardId = Number(cardOrId);
    if (Number.isFinite(cardId) && cardId > 0) {
      const match = (this.searchResults || []).find((c) => Number(c.id) === cardId);
      return match || { id: cardId };
    }

    return null;
  }

  private resetCardDetailsState(): void {
    this.activeCard = null;
    this.drawerPatients = [];
    this.settlePatient = null;
    this.drawerLoading = false;
    this.highlightPatientId = null;
  }

  closeCardDetails(): void {
    this.drawerVisible = false;
  }

  onDrawerHide(): void {
    this.resetCardDetailsState();
  }

  private patientMatchesQuery(patient: any, card: CardSearchHit, query: string): boolean {
    if (!query) {
      return true;
    }
    const haystack = [
      patient?.full_name,
      patient?.salutation,
      patient?.patient_number,
      patient?.contact_number,
      card?.number,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return query
      .split(/\s+/)
      .filter(Boolean)
      .every((word) => haystack.includes(word));
  }

  getPatientDisplayName(patient: any): string {
    if (!patient) {
      return 'No patients on this card';
    }
    const salutation = (patient.salutation || '').trim();
    const name = (patient.full_name || patient.fullName || '').trim();
    return [salutation, name].filter(Boolean).join(' ') || 'Unnamed patient';
  }

  getPatientInitials(patient: any): string {
    const name = (patient?.full_name || patient?.fullName || '').trim();
    if (!name) {
      return '?';
    }
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }

  formatStatus(status: any): string {
    const value = String(status || '').trim();
    if (!value) {
      return 'Unknown';
    }
    return value
      .toLowerCase()
      .split(/[_\s]+/)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  getStatusClass(status: any): string {
    const value = String(status || '').toLowerCase();
    if (value === 'active') return 'is-active';
    if (value === 'inactive') return 'is-inactive';
    return 'is-default';
  }

  formatRupee(value: any): string {
    const amount = Number(value ?? 0);
    if (!Number.isFinite(amount)) {
      return '0';
    }
    return amount.toLocaleString('en-IN');
  }

  refreshActiveCard(): void {
    if (!this.activeCard?.id) {
      return;
    }
    this.openCard(this.activeCard.id);
  }

  openCreateCard(): void {
    this.createCardNumber = '';
    this.createDialogVisible = true;
    this.loadSuggestedCardNumber((number) => {
      if (!(this.createCardNumber || '').trim()) {
        this.createCardNumber = number;
      }
    });
  }

  closeCreateCard(): void {
    this.createDialogVisible = false;
    this.createCardNumber = '';
    this.createBusy = false;
  }

  submitCreateCard(): void {
    const number = (this.createCardNumber || '').trim().toUpperCase();
    const clinicId = Number(this.workContext.getClinicId() || 0);
    this.createBusy = true;
    this.cardsService.createCard({
      number: number || undefined,
      clinic_id: clinicId > 0 ? clinicId : undefined,
      status: 'active',
      total_dues: 0,
    } as any).subscribe({
      next: (res: any) => {
        this.createBusy = false;
        if (!res?.success) {
          this.toaster.error(res?.message || 'Failed to create card.');
          return;
        }
        this.toaster.success(res.message || 'Card created.');
        this.closeCreateCard();
        const created = res.data;
        if (created?.id) {
          this.searchTerm = created.number || number;
          this.searchSubject.next(this.searchTerm);
          this.openCard(created);
        }
      },
      error: (err) => {
        this.createBusy = false;
        console.error('Create card error:', err);
        this.toaster.error(err?.error?.message || 'Failed to create card.');
      },
    });
  }

  openMove(patient: any, fromCard?: any): void {
    const patientId = Number(patient?.id);
    if (!Number.isFinite(patientId) || patientId <= 0) {
      this.toaster.error('Patient id is missing. Please search again.');
      return;
    }
    this.movePatient = { ...patient, id: patientId };
    this.moveFromCard = fromCard || this.activeCard;
    this.moveOption = 'existing';
    this.moveTargetQuery = '';
    this.moveTargetCard = null;
    this.moveTargetResults = [];
    this.moveTargetSearching = false;
    this.moveNewCardNumber = '';
    this.suggestedCardNumber = '';
    this.moveDialogVisible = true;
  }

  closeMove(): void {
    this.moveDialogVisible = false;
    this.moveBusy = false;
    this.movePatient = null;
    this.moveFromCard = null;
    this.moveOption = 'existing';
    this.moveTargetQuery = '';
    this.moveTargetCard = null;
    this.moveTargetResults = [];
    this.moveTargetSearching = false;
    this.moveNewCardNumber = '';
    this.suggestedCardNumber = '';
    this.suggestingCardNumber = false;
  }

  setMoveOption(option: 'existing' | 'new'): void {
    this.moveOption = option;
    if (option === 'existing') {
      this.moveNewCardNumber = '';
      this.suggestedCardNumber = '';
    } else {
      this.moveTargetQuery = '';
      this.moveTargetCard = null;
      this.moveTargetResults = [];
      this.loadSuggestedCardNumber((number) => {
        this.suggestedCardNumber = number;
        if (!(this.moveNewCardNumber || '').trim()) {
          this.moveNewCardNumber = number;
        }
      });
    }
  }

  private loadSuggestedCardNumber(onReady?: (number: string) => void): void {
    const clinicId = Number(this.workContext.getClinicId() || 0);
    if (clinicId <= 0) {
      this.toaster.warn('Select a clinic in the work context to suggest the next card number.');
      return;
    }

    this.suggestingCardNumber = true;
    this.cardsService.getNextCardNumber(clinicId).subscribe({
      next: (res: any) => {
        this.suggestingCardNumber = false;
        const number = String(res?.data?.number || '').trim().toUpperCase();
        if (!number) {
          return;
        }
        onReady?.(number);
      },
      error: (err) => {
        this.suggestingCardNumber = false;
        console.error('Next card number error:', err);
        this.toaster.error(err?.error?.message || 'Could not suggest the next card number.');
      },
    });
  }

  useSuggestedCardNumber(): void {
    if (!this.suggestedCardNumber) {
      this.loadSuggestedCardNumber((number) => {
        this.suggestedCardNumber = number;
        this.moveNewCardNumber = number;
      });
      return;
    }
    this.moveNewCardNumber = this.suggestedCardNumber;
  }

  onMoveTargetQueryChange(value: string): void {
    this.moveTargetQuery = value;
    if (this.moveTargetCard && !(value || '').trim()) {
      this.moveTargetCard = null;
    }
    this.moveSearchSubject.next(value);
  }

  selectMoveTarget(card: any): void {
    this.moveTargetCard = card;
    this.moveTargetQuery = card?.number || '';
    this.moveTargetResults = [];
  }

  clearMoveTarget(): void {
    this.moveTargetCard = null;
    this.moveTargetQuery = '';
    this.moveTargetResults = [];
    this.moveSearchSubject.next('');
  }

  private mapMoveTargetOption(card: any): any {
    const patients = Array.isArray(card.patients) ? card.patients : [];
    const names = patients.map((p: any) => p.full_name).filter(Boolean);
    return {
      ...card,
      previewNames: names.slice(0, 2),
      extraCount: Math.max(0, names.length - 2),
    };
  }

  getMoveMemberSummary(card: any): string {
    if (!card) {
      return '';
    }
    const count = Number(card.patient_count ?? card.previewNames?.length ?? 0);
    const names = Array.isArray(card.previewNames) ? card.previewNames : [];
    if (count <= 0 && !names.length) {
      return 'Empty card';
    }
    if (!names.length) {
      return `${count} member${count === 1 ? '' : 's'}`;
    }
    const head = names.join(', ');
    const extra = Number(card.extraCount || 0);
    const who = extra > 0 ? `${head} +${extra} more` : head;
    return `${count} member${count === 1 ? '' : 's'} · ${who}`;
  }

  get canConfirmMove(): boolean {
    if (!this.movePatient?.id) {
      return false;
    }
    if (this.moveOption === 'existing') {
      return !!this.moveTargetCard?.id;
    }
    // New card can use typed number or auto-generate on submit
    return true;
  }

  confirmMove(): void {
    if (!this.canConfirmMove || this.moveBusy) {
      return;
    }

    const createNewCard = this.moveOption === 'new';
    const clinicId = Number(this.workContext.getClinicId() || 0);
    const payload = {
      patient_id: this.movePatient.id,
      target_card_id: createNewCard ? null : this.moveTargetCard.id,
      new_card_number: createNewCard ? ((this.moveNewCardNumber || '').trim().toUpperCase() || null) : null,
      create_new_card: createNewCard,
      clinic_id: createNewCard && clinicId > 0 ? clinicId : undefined,
    };

    this.moveBusy = true;
    this.cardsService.movePatient(payload).subscribe({
      next: (res: any) => {
        this.moveBusy = false;
        this.toaster.success(res?.message || 'Patient moved successfully.');
        const newCard = res?.data?.new_card;
        this.closeMove();
        if ((this.searchTerm || '').trim()) {
          this.searchSubject.next(this.searchTerm);
        }
        if (newCard?.id) {
          this.openCard(newCard);
        } else if (this.activeCard?.id) {
          this.refreshActiveCard();
        }
      },
      error: (err) => {
        this.moveBusy = false;
        console.error('Move patient error:', err);
        this.toaster.error(err?.error?.message || 'Failed to move patient');
      },
    });
  }

  toggleSettle(patient: any): void {
    if (this.settlePatient?.id === patient?.id) {
      this.settlePatient = null;
      return;
    }
    this.settlePatient = {
      ...patient,
      paymentMode: null,
      cashAmount: null,
      onlineAmount: null,
    };
  }

  closeSettle(): void {
    this.settlePatient = null;
  }

  private duesSettlementCeiling(patient: any): number {
    const personal = Math.max(0, Number(patient?.personal_dues) || 0);
    const cardTotal = Math.max(0, Number(this.activeCard?.total_dues) || 0);
    if (personal > 0 && cardTotal > 0) {
      return Math.min(personal, cardTotal);
    }
    return Math.max(personal, cardTotal);
  }

  private rejectDuesOverpayment(amount: number, patient: any): boolean {
    const ceiling = this.duesSettlementCeiling(patient);
    if (amount <= ceiling) {
      return false;
    }
    this.toaster.warn(
      `Payment cannot exceed ₹${ceiling.toLocaleString('en-IN')} (personal and card dues).`,
      'Amount too high'
    );
    return true;
  }

  settleCashDues(): void {
    const p = this.settlePatient;
    if (!p?.id) {
      return;
    }
    if (!p.cashAmount || p.cashAmount <= 0) {
      this.toaster.warn('Enter a valid cash amount before saving.', 'Invalid Amount');
      return;
    }
    if (this.rejectDuesOverpayment(Number(p.cashAmount), p)) {
      return;
    }

    this.cardsService.settleCashDues({
      patient_id: p.id,
      amount: p.cashAmount,
    }).subscribe({
      next: () => {
        this.toaster.success('Cash payment recorded successfully.', 'Dues Settled');
        this.closeSettle();
        this.refreshActiveCard();
        if ((this.searchTerm || '').trim()) {
          this.searchSubject.next(this.searchTerm);
        }
      },
      error: (err) => {
        this.toaster.error(err?.error?.message || 'Failed to record cash payment.');
      },
    });
  }

  initiateCardDuesPayment(): void {
    const patient = this.settlePatient;
    if (!patient?.id) {
      this.toaster.error('No valid patient selected for dues payment.', 'Invalid Patient');
      return;
    }

    const amount = parseFloat(patient.onlineAmount || 0);
    if (isNaN(amount) || amount <= 0) {
      this.toaster.warn('Please enter a valid amount greater than zero.', 'Invalid Amount');
      return;
    }
    if (this.rejectDuesOverpayment(amount, patient)) {
      return;
    }

    const loggedInUserRaw = sessionStorage.getItem('loggedInUser');
    let loggedInUser: any = null;
    try {
      loggedInUser = loggedInUserRaw ? JSON.parse(loggedInUserRaw) : null;
    } catch {
      loggedInUser = null;
    }

    const payload = {
      patient_id: patient.id,
      amount,
      doctor_id: loggedInUser?.id ?? null,
    };

    this.cardsService.ccAvenueCardDuesPayment(payload).subscribe((res: any) => {
      if (!res || !res.ccavenueUrl || !res.encRequest || !res.accessCode) {
        this.toaster.error('Invalid payment initialization response.', 'Payment Error');
        return;
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = res.ccavenueUrl;
      form.target = 'paymentPopup';

      const fields = {
        encRequest: res.encRequest,
        access_code: res.accessCode,
      };
      Object.entries(fields).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value as string;
        form.appendChild(input);
      });

      const popup = window.open('', 'paymentPopup', 'width=800,height=600');
      if (!popup) {
        this.toaster.warn('Please allow popups to continue payment.', 'Popup Blocked');
        return;
      }

      document.body.appendChild(form);
      form.submit();
      form.remove();

      if (!this.cardPaymentListenerAttached) {
        this.cardPaymentListenerAttached = true;
        window.addEventListener('message', (event) => {
          const allowedOrigins = ['https://clinic.drsubodhbhade.com', window.location.origin];
          if (!allowedOrigins.includes(event.origin)) return;

          const data = event.data;
          const status = String(data?.status || data?.payment_status || '').trim().toLowerCase();
          if (!status) return;

          switch (status) {
            case 'success':
              this.toaster.add({
                severity: 'success',
                summary: 'Payment Successful',
                detail: `INR ${data.amount} settled successfully for patient dues.`,
              });
              this.closeSettle();
              this.refreshActiveCard();
              if ((this.searchTerm || '').trim()) {
                this.searchSubject.next(this.searchTerm);
              }
              break;
            case 'failure':
              this.toaster.error('The transaction was unsuccessful. Please try again.', 'Payment Failed');
              break;
            case 'aborted':
              this.toaster.warn('You cancelled the payment.', 'Payment Aborted');
              break;
            case 'invalid':
              this.toaster.error('Payment request was invalid or corrupted.', 'Invalid Payment');
              break;
            case 'timeout':
              this.toaster.warn('The payment session expired or timed out. Please try again.', 'Payment Timeout');
              break;
            default:
              this.toaster.info('Payment status: ' + status, 'Payment Status');
              break;
          }
          popup?.close();
        });
      }
    }, (err: any) => {
      this.toaster.error(err?.error?.message || 'Could not start dues payment.', 'Payment Error');
    });
  }

  confirmDeleteCard(item: any): void {
    if (!item?.id) {
      return;
    }
    this.confirmationService.confirm({
      key: 'cardDeleteDialog',
      header: 'Delete Card',
      message: `Delete card ${item?.number || ''}? Patients on this card may be affected. This cannot be undone.`,
      icon: 'pi pi-trash',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      rejectIcon: 'pi pi-times',
      acceptIcon: 'pi pi-trash',
      rejectButtonStyleClass: 'card-delete-reject-btn',
      acceptButtonStyleClass: 'card-delete-accept-btn',
      defaultFocus: 'reject',
      accept: () => this.deleteCard(item),
    });
  }

  private deleteCard(item: any): void {
    this.cardsService.deleteCard(item).subscribe({
      next: () => {
        this.toaster.success('Card deleted successfully');
        if (this.activeCard?.id === item.id) {
          this.closeCardDetails();
        }
        if ((this.searchTerm || '').trim()) {
          this.searchSubject.next(this.searchTerm);
        } else {
          this.searchResults = [];
        }
      },
      error: () => {
        this.toaster.error('Unable to delete card.');
      },
    });
  }
}
